import { useCallback, useEffect, useState } from 'react';
import { BfIconSparkle, BfIconBookmark, BfIconShare, BfIconLink } from './BfIcons';
import { bfHaptic } from './BfHooks';

/**
 * BfSmartHighlight — Apple Books-стиль мини-меню при выделении текста.
 *
 * Слушает выделение внутри ridder-контейнера (containerSelector).
 * При появлении непустого selection показывает floating capsule
 * под выделением (снизу) с действиями: Объяснить · Сохранить · Поделиться · Копировать.
 *
 * Не модальный — не блокирует фон, исчезает при сбросе выделения.
 */
export default function BfSmartHighlight({
  containerSelector = '.bf-reader',
  onAskAI,
  onSave,
  onShare,
  /** Пользователь выделил текст — снять режим «активный стих» с панелью снизу. */
  onTextSelectionActive,
}) {
  const [state, setState] = useState(null); // { x, y, placement, text }

  const measure = useCallback(() => {
    const sel = typeof window !== 'undefined' ? window.getSelection?.() : null;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setState(null);
      return;
    }
    const text = sel.toString().trim();
    if (!text || text.length < 2) {
      setState(null);
      return;
    }
    // Проверяем, что выделение внутри читалки
    const container = document.querySelector(containerSelector);
    if (!container) {
      setState(null);
      return;
    }
    const anchorNode = sel.anchorNode;
    if (!anchorNode || !container.contains(anchorNode)) {
      setState(null);
      return;
    }

    const range = sel.getRangeAt(0);
    const rects = range.getClientRects();
    if (!rects || rects.length === 0) {
      setState(null);
      return;
    }
    const first = rects[0];
    const last = rects[rects.length - 1];
    const cx = (first.left + last.right) / 2;
    const top = first.top;
    const bottom = last.bottom;
    const gap = 10;
    const menuH = 44;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 600;
    const spaceBelow = viewportH - bottom;
    const spaceAbove = top;
    /* По умолчанию — под выделением; вверх только если снизу не влезает. */
    const placement =
      spaceBelow < menuH + gap && spaceAbove > spaceBelow ? 'above' : 'below';
    const y = placement === 'above' ? top - gap : bottom + gap;
    setState((prev) => {
      if (!prev) {
        onTextSelectionActive?.();
      }
      return { x: cx, y, placement, text };
    });
  }, [containerSelector, onTextSelectionActive]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => {
      window.requestAnimationFrame(measure);
    };
    document.addEventListener('selectionchange', onChange);
    window.addEventListener('scroll', onChange, { passive: true });
    window.addEventListener('resize', onChange);
    return () => {
      document.removeEventListener('selectionchange', onChange);
      window.removeEventListener('scroll', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, [measure]);

  const clearSelection = useCallback(() => {
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      /* no-op */
    }
    setState(null);
  }, []);

  if (!state) return null;

  const handle = (fn) => () => {
    bfHaptic('light');
    fn?.(state.text);
    clearSelection();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(state.text);
      bfHaptic('success');
    } catch {
      /* no-op */
    }
    clearSelection();
  };

  return (
    <div
      className="bf-smart-highlight"
      style={{
        position: 'fixed',
        left: state.x,
        top: state.y,
        transform: `translate(-50%, ${state.placement === 'above' ? '-100%' : '0'})`,
        zIndex: 90,
      }}
      onPointerDown={(e) => e.stopPropagation()}
      role="toolbar"
      aria-label="Selection actions"
    >
      <button
        type="button"
        className="bf-smart-highlight__btn bf-smart-highlight__btn--primary"
        onClick={handle(onAskAI)}
      >
        <BfIconSparkle />
        <span>Объяснить</span>
      </button>
      <span className="bf-smart-highlight__divider" aria-hidden="true" />
      <button type="button" className="bf-smart-highlight__btn" onClick={handle(onSave)} aria-label="Сохранить">
        <BfIconBookmark />
      </button>
      <button type="button" className="bf-smart-highlight__btn" onClick={handle(onShare)} aria-label="Поделиться">
        <BfIconShare />
      </button>
      <button type="button" className="bf-smart-highlight__btn" onClick={handleCopy} aria-label="Копировать">
        <BfIconLink />
      </button>
    </div>
  );
}
